import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Flag, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_CATEGORIES = [
  { value: 'spam', label: 'Spam', description: 'Repetitive or promotional content' },
  { value: 'scam', label: 'Scam/Fraud', description: 'Suspicious activity or fraud attempts' },
  { value: 'inappropriate', label: 'Inappropriate Content', description: 'Offensive or inappropriate behavior' },
  { value: 'fake_listing', label: 'Fake Listing', description: 'Misleading or false product information' },
  { value: 'harassment', label: 'Harassment', description: 'Abusive or threatening behavior' },
  { value: 'other', label: 'Other', description: 'Other violations' }
];

const ReportDialog = ({ 
  open, 
  onClose, 
  type = 'user', // 'user' or 'listing'
  targetId, 
  targetName 
}) => {
  const [category, setCategory] = useState('');
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    
    if (!category) {
      setError('Please select a category');
      return;
    }
    if (reason.trim().length < 10) {
      setError('Please provide a reason (at least 10 characters)');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please login to submit a report');
      return;
    }

    setSubmitting(true);

    try {
      const endpoint = type === 'user' 
        ? `${API_URL}/api/moderation/report/user`
        : `${API_URL}/api/moderation/report/listing`;
      
      const body = type === 'user' 
        ? { reported_user_id: targetId, category, reason, details }
        : { listing_id: targetId, category, reason, details };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setError(data.error || data.detail || 'Failed to submit report');
      }
    } catch (err) {
      setError('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setCategory('');
    setReason('');
    setDetails('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-red-500" />
            Report {type === 'user' ? 'User' : 'Listing'}
          </DialogTitle>
          <DialogDescription>
            Report {targetName || (type === 'user' ? 'this user' : 'this listing')} for violating community guidelines
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-green-700">Report Submitted</h3>
            <p className="text-gray-600 mt-2">
              Thank you for helping keep our community safe. We'll review your report shortly.
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Category Selection */}
            <div className="space-y-2">
              <Label>Report Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div>
                        <span className="font-medium">{cat.label}</span>
                        <span className="text-xs text-gray-500 ml-2">{cat.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">Reason</Label>
              <Textarea
                id="reason"
                placeholder="Briefly describe the issue..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={200}
              />
              <p className="text-xs text-gray-500">{reason.length}/200 (minimum 10)</p>
            </div>

            {/* Additional Details */}
            <div className="space-y-2">
              <Label htmlFor="details">Additional Details (Optional)</Label>
              <Textarea
                id="details"
                placeholder="Provide any additional information..."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-gray-500">{details.length}/500</p>
            </div>

            {/* Submit Button */}
            <div className="flex space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !category || reason.length < 10}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Flag className="h-4 w-4 mr-2" />
                    Submit Report
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReportDialog;
